import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import { format, parseISO } from 'date-fns'

import type { HonorariumNote, HonorariumNoteItem, Profile } from '@/types/domain'

const stripEmojis = (str: string) => {
    return str.replace(/(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/g, '').replace(/\s+/g, ' ').trim()
}

export class HonorariumPdfService {
    static async generate(
        note: HonorariumNote,
        items: (HonorariumNoteItem & { session?: any })[],
        trainer: Profile
    ): Promise<void> {
        const doc = new jsPDF()
        const pageWidth = doc.internal.pageSize.getWidth()
        const pageHeight = doc.internal.pageSize.getHeight()

        // Logo
        try {
            const logoUrl = '/motionlab.png'
            doc.addImage(logoUrl, 'PNG', 20, 15, 30, 25)
        } catch (e) {
            console.error('Failed to load logo', e)
        }

        // Branding / Header
        doc.setFontSize(22)
        doc.setTextColor(40)
        doc.text('motionLAB', 55, 25)

        doc.setFontSize(10)
        doc.setTextColor(100)
        doc.text('Mag. Christina Hohenwarter', 55, 32)
        doc.text('Mohnweg 3, 9161 Maria Rain', 55, 37)

        // Right aligned header info
        doc.setFontSize(12)
        doc.setTextColor(0)
        doc.text('HONORARNOTE', pageWidth - 20, 25, { align: 'right' })

        doc.setFontSize(10)
        const invoiceNr = note.invoice_number || note.id.split('-')[0].toUpperCase()
        doc.text(`Nr: ${invoiceNr}`, pageWidth - 20, 32, { align: 'right' })
        doc.text(`Datum: ${format(new Date(), 'dd.MM.yyyy')}`, pageWidth - 20, 37, { align: 'right' })

        // Trainer Info
        doc.setFontSize(11)
        doc.text('Empfänger:', 20, 55)
        doc.setFont('helvetica', 'bold')
        doc.text(trainer.full_name || '', 20, 60)
        doc.setFont('helvetica', 'normal')
        doc.text(`${trainer.street || ''} ${trainer.house_number || ''}`, 20, 65)
        doc.text(`${trainer.postal_code || ''} ${trainer.city || ''}`, 20, 70)

        // Period Info
        doc.text(`Zeitraum: ${format(parseISO(note.period_start), 'dd.MM.yyyy')} bis ${format(parseISO(note.period_end), 'dd.MM.yyyy')}`, 20, 85)

        // Items Table
        const tableData = items.map((item) => {
            const dateStr = item.session?.starts_at 
                ? format(parseISO(item.session.starts_at), 'dd.MM.yyyy HH:mm')
                : 'Unbekannt'
            const courseName = item.session?.courses?.name || 'Unbekannter Kurs'
            const courseLevel = item.session?.courses?.level ? ` (${item.session.courses.level})` : ''
            
            return [
                dateStr,
                stripEmojis(`${courseName}${courseLevel}`),
                (item.participant_count || 0).toString(),
                `${(item.amount_cents / 100).toFixed(2)} €`,
            ]
        })

        autoTable(doc, {
            startY: 95,
            head: [['Datum', 'Kurs', 'Teilnehmer', 'Betrag']],
            body: tableData,
            headStyles: { fillColor: [40, 40, 40] },
            margin: { left: 20, right: 20 },
            columnStyles: {
                0: { cellWidth: 35 },
                1: { cellWidth: 'auto' },
                2: { cellWidth: 25, halign: 'center' },
                3: { cellWidth: 25, halign: 'right' },
            },
            styles: {
                fontSize: 9,
                cellPadding: 3,
                overflow: 'linebreak',
            },
        })

        // Total
        const finalY = (doc as any).lastAutoTable.finalY + 10
        doc.setFont('helvetica', 'bold')
        doc.text(`Gesamtbetrag: ${(note.total_amount_cents / 100).toFixed(2)} €`, pageWidth - 20, finalY, { align: 'right' })

        // Payment Info
        doc.setFont('helvetica', 'normal')
        doc.setFontSize(10)
        doc.text('Der Betrag wird auf das hinterlegte Konto überwiesen.', 20, finalY + 20)
        doc.text('Zahlungsziel: 14 Tage nach Erhalt dieser Honorarnote.', 20, finalY + 25)

        // Footer
        doc.setFontSize(8)
        doc.setTextColor(150)
        doc.text('Dies ist eine automatisch generierte Honorarnote.', pageWidth / 2, pageHeight - 25, { align: 'center' })
        doc.setFont('helvetica', 'bold')
        doc.text('IBAN: AT12 6000 0902 1007 5789', pageWidth / 2, pageHeight - 20, { align: 'center' })
        doc.setFont('helvetica', 'normal')
        doc.text('(Kontohinhaber: Mag. Christina Hohenwarter)', pageWidth / 2, pageHeight - 15, { align: 'center' })

        // Download
        doc.save(`Honorarnote_${trainer.full_name?.replace(/\s+/g, '_')}_${format(parseISO(note.period_start), 'yyyy-MM-dd')}.pdf`)
    }
}
